<template>
  <div>

    <Head :title="`${order.customer} - show order`" />
    <div class="flex items-center justify-between mb-6">
      <h1 class="mb-8 text-3xl font-bold">
        <Link class="text-orange-600 hover:text-orange-500" href="/online-orders">Online Sales Orders</Link> &raquo;{{ order.order_no }}: {{
        order.customer }}
      </h1>
      <Link v-if="order.status == 'Placed' || order.status == 'Verified' || order.status == 'Pending Confirmation'" class="btn-orange text-white-400 hover:text-white-600" :href="`/online-orders/${order.id}/edit`">Edit</Link>
      <Link
        v-if="$page.props.app.env !=='production'"
        v-show="replaceRescheduleStatuses.includes(order.status)"
        class="btn-orange text-white-400 hover:text-white-600" :href="`/online-orders/${order.id}/replace/reschedules`">Replace/Reschedule</Link>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <form>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 md:px-6 sm:flex md:flex modal-wrapper w-full">
          <h1>
            <strong>Status:</strong> 
            <span v-if="order.status === 'Declined'" class="declined"> [Declined][Reason: {{ order.decline_reason
              }}] </span>
            <span v-else class="approved"> [{{ order.status }}] </span>
          </h1>
        </div>
        <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
          <div class="bg-orange-50 rounded-md shadow overflow-x-auto mb-4">
            <div class="grid grid-cols-1 gap-4 p-1">
              <div class="pt-2 p-2">
                <h1 style="font-size:20px; font-weight: bold;">
                  General Information
                </h1>
              </div>
            </div>
          </div>
          <div class="mb-4 overflow-x-auto">
            <div class="grid grid-cols-1 gap-4 pr-8 pl-2 pt-2 pb-2">
              <div class="flex flex-wrap">
                <label for="order_type" class="pr-6 w-1/2 lg:w-1/2"><strong>Order Type:</strong> {{ order.order_type }}</label>
                <label for="freshchat_conversation_id" class="pr-6 w-1/2 lg:w-1/2"><strong>Freshchat Conversation ID:</strong>
                  <a
                    v-if="order.freshchat_conversation_id"
                    class="text-orange-600 hover:text-orange-500"
                    target="_blank"
                    :href="`${order.freshchat_messaging_url}`"
                  >
                    {{ order.freshchat_conversation_id }}
                  </a>
                  <a v-else href="#">
                    {{ order.freshchat_conversation_id }}
                  </a>
                </label>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-4 pr-8 pl-2 pt-2 pb-2">
              <div class="flex flex-wrap">
                <label for="payment_detail" class="pr-6 w-1/2 lg:w-1/2"><strong>Payment Detail:</strong> {{ order.payment_details }}</label>
                <label for="shipping_type" class="pr-6 w-1/2 lg:w-1/2"><strong>Shipping Type:</strong> {{ order.shipping_type }}</label>
              </div>
            </div>
          <div v-if="order.shipping_type === 'Pickup'" class="grid grid-cols-1 gap-4 pr-8 pl-2 pt-2 pb-2">
            <div class="flex flex-wrap">
              <label for="pickup_location" class="pr-6 w-1/2 lg:w-1/2"><strong>Pickup Location: </strong>{{ order.pickup_location }}</label>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 pr-8 pl-2 pt-2 pb-2">
            <div class="flex flex-wrap">
              <label for="point_of_sale" class="pr-6 w-1/2 lg:w-1/2"><strong>Point of Sale:</strong> {{ order.point_of_sale }}</label>
              <label for="delivery_date" class="pr-6 w-1/2 lg:w-1/2"><strong>Delivery Date:</strong> {{ order.delivery_date }}</label>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 pr-8 pl-2 pt-2 pb-2">
            <div class="flex flex-wrap">
              <label for="referral" class="pr-6 w-1/2 lg:w-1/2"><strong>Referred By:</strong> {{ order.referral }}</label>
              <label for="agent" class="pr-6 w-1/2 lg:w-1/2"><strong>Responsible Agent:</strong> {{ order.agent }}</label>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 pr-8 pl-2 pt-2 pb-2">
            <div class="flex flex-wrap">             
              <label v-if="order.partial_delivery" class="pr-6 w-1/2 lg:w-1/2"><strong>Partial Delivery:</strong> Yes</label>
              <label v-else class="pr-6 w-1/2 lg:w-1/2"><strong>Partial Delivery:</strong> No</label>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 pr-8 pl-2 pt-2 pb-2">
            <div class="flex flex-wrap">             
              <label v-if="order.assisting_agent" class="pr-6 w-1/2 lg:w-1/2"><strong>Assisting Agent:</strong> {{ order.assisting_agent }}</label>
            </div>
          </div>
          
          </div>
        </div>
        <div v-if="order.details.length > 0" class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
          <div class="bg-orange-50 rounded-md shadow overflow-x-auto mb-4">
            <div class="grid grid-cols-1 gap-4 p-1">
              <div class="pt-2 p-2">
                <h1 style="font-size:20px; font-weight: bold;">
                  Ordered Details
                </h1>
              </div>
            </div>
          </div>
          <div class="mb-4 overflow-x-auto">
            <table class="w-full whitespace-nowrap">
              <tr class="text-left font-bold">
                <th class="pb-3 pt-3 px-3">#</th>
                <th class="pb-3 pt-3 px-3">SKU</th>
                <th class="pb-3 pt-3 px-3">Description</th>
                <th class="pb-3 pt-3 px-3">Quantity</th>
                <th class="pb-3 pt-3 px-3">Unit Price</th>
                <th class="pb-3 pt-3 px-3">Discount (%)</th>
                <th class="pb-3 pt-3 px-3 text-end float-right">Amount</th>
              </tr>
              <tr v-for="(item, index) in order.details" :key="index">
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-center px-3">{{ index + 1 }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-left px-3">{{ item.sku }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3"
                  style="word-wrap: break-word !important; word-break: break-all !important; white-space: normal !important; width: 30%;">
                  <span class="flex items-left px-3">{{ item.description }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-center justify-center px-3">{{ item.quantity }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-center justify-center px-3">{{ item.unit_cost }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-center justify-center px-3">{{ item.discount_rate }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-end px-3 items-end justify-end">{{ item.amount }}</span>
                </td>
              </tr>
              <tr v-if="order.transport && order.transport > 0">
                <td class="w-px border-t pb-3 pt-3" width="2%"><span class="flex items-center px-3"> {{
      order.details.length + 1 }}</span></td>
                <td class="w-px border-t pb-3 pt-3" width="10%">
                  <span class="flex items-left px-3">{{ order.transport_name }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3" width="30%">
                  <span class="flex items-left px-3">{{ order.transport_description }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-center justify-center px-3">1</span>
                </td>
                <td class="w-px border-t pb-3 pt-3" width="5%">
                  <span class="flex items-center justify-center px-3">{{ order.transport_amount }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3" width="5%">
                  <span class="flex items-center justify-center px-3">0</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-end px-3 items-end justify-end">{{ order.transport_amount }}</span>
                </td>
              </tr>
              <tr v-if="order.voucherInfo && order.voucherInfo.code && order.has_discount_voucher == 0">
                <td class="pb-3 pt-3  border-t border-b"><span class="flex items-center px-3">-</span></td>
                <td class="pb-3 pt-3  border-t border-b" colspan="5"> Voucher Code: #{{ order.voucherInfo.code }} -
                  {{ order.voucherInfo.value }} {{ order.voucherInfo.type === 'Percentage' ? '%' : '' }}</td>
                <td class="pb-3 pt-3  border-t border-b">
                  <span class="flex items-end px-3 items-end justify-end">{{ order.voucherInfo.voucher_amount
                    }}</span>
                </td>
              </tr>
              <tr>
                <th class="pb-3 pt-3  border-t border-b">Total</th>
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b">
                  <span class="flex items-end px-3 items-end justify-end">{{ order.amount }}</span>
                </th>
              </tr>
            </table>
          </div>
          <div class="bg-orange-50 rounded-md shadow overflow-x-auto mb-4">
            <div class="grid grid-cols-1 gap-4 p-1">
              <div class="pt-2 p-2">
                <h1 style="font-size:20px; font-weight: bold;">
                  Payment Details
                </h1>
              </div>
            </div>
          </div>
          <div v-if="order.payments.length" class="mb-4 overflow-x-auto">
            <table class="w-full whitespace-nowrap">
              <tr class="text-left font-bold">
                <th class="pb-3 pt-3 px-3">#</th>
                <th class="pb-3 pt-3 px-3">Date</th>
                <th class="pb-3 pt-3 px-3">Payment Mode</th>
                <th class="pb-3 pt-3 px-3">Reference</th>
                <th class="pb-3 pt-3 px-3">Initiated By</th>
                <th class="pb-3 pt-3 px-3">Phone Number</th>
                <th class="flex items-end px-3 items-end justify-end">Amount</th>
              </tr>
              <tr v-for="(item, index) in order.payments" :key="index">
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-center px-3">{{ index + 1 }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-left px-3">{{ item.date }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-left px-3">{{ item.provider }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3 break-all"
                  style="word-wrap: break-word !important; word-break: break-all !important; white-space: normal !important; width: 30%;">
                  <p class="px-3">{{ item.reference }}</p>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-center px-3">{{ item.user }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-center px-3">{{ item.phone_number }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-end px-3 items-end justify-end">{{ item.amount }}</span>
                </td>
              </tr>
              <tr>
                <th class="pb-3 pt-3  border-t border-b">Total</th>
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b" />
                <th class="pb-3 pt-3  border-t border-b">
                  <span class="flex items-end px-3 items-end justify-end">{{ order.total_payments }}</span>
                </th>
              </tr>
            </table>
          </div>
          <div class="bg-orange-50 rounded-md shadow overflow-x-auto mb-4">
            <div class="grid grid-cols-1 gap-4 p-1 pl-2">
              <div class="pt-2 p-2">
                <h1 style="font-size:20px; font-weight: bold;">
                  Notes
                </h1>
              </div>
            </div>
          </div>
          <div class="mb-4">
            <p v-show="order.notes">{{ order.notes }}</p>
          </div>
        </div>
        <div v-else class="p-5">No order details not found.</div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <a 
                v-if="((order.invoice && order.invoice.taxilla_status === 'completed'))"
                class="btn btn-orange text-xs cursor-pointer ml-1 mr-1"
                :href="`/download-taxilla-pdf-invoices/${order.invoice.id}`"
                target="__blank"
                title="Download">
                <span>Download Invoice</span>
              </a>
            </span>
            <span v-show="amountToPay > 0" class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button v-if="!(order.status =='Declined' || order.status =='Cancelled')" type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5 ml-1 mr-1" @click="makePaymentModel(order)">
                Initiate Payment
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button v-if="!(orderObj.status =='Declined' || orderObj.status =='Cancelled')" type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5 ml-1 mr-1" @click="addPayment(order)">
                Add Payment
              </button>
            </span>
            <span v-show="order.status =='Pending Confirmation'" class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5 ml-1 mr-1" @click="confirmPlacedOrderModal(order)">
                Confirm
              </button>
            </span>
            <span v-if="order.status == 'Placed' || order.status == 'Verified' || order.status == 'Pending Confirmation'" class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5 ml-1 mr-1" @click="openDeletePrompt(order)">
                Cancel
              </button>
            </span>
          </div>
      </form>
    </div>
  </div>
  <div v-if="isMakePaymentOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex flex-wrap w-full items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-2 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 md:px-6 sm:flex md:flex modal-wrapper w-full">
            <h1>Make Payment for order#{{ form.order_on }}</h1>
          </div>
          <div v-if="!merchantRequestId" class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <div
                class="flex flex-wrap w-full w-full"
              >
                <div class="pb-2 pr-6 w-full lg:w-full">
                  <text-input
                    v-model="form.phone_number"
                    :error="phonenumberError"
                    placeholder="Phone Number"
                    label="Phone Number"
                  />
                </div>
                <div class="pb-2 pr-6 w-full lg:w-full">
                  <text-input
                    v-model="form.amount"
                    :error="amountError"
                    class="pb-2"
                    placeholder="Amount"
                    label="Amount"
                  />
                </div>
              </div>
            </div>
            <div v-if="loading" style="color:green"><p> Processing, kindly wait...</p></div>
            <div v-else>
              <p v-if="status == 1 && mpesa_response" style="color:green">
                <div class="flex items-center justify-between mb-6">
                  <p>{{ mpesa_response }}</p>
                  <span class="mt-3 flex w-1/3 rounded-md shadow-sm sm:mt-0 sm:w-auto">
                    <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2
                  bg-green-500 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500
                  focus:outline-none focus:border-green-700 focus:shadow-outline-green sm:text-sm
                  sm:leading-5" @click="checkPayment()">
                      Refresh
                    </button>
                  </span>
                </div>
              </p>
              <p v-else-if="status == 0 && mpesa_response" style="color:red">{{ mpesa_response }}</p>
              <p v-else></p>
            </div>
          </div>
          <div v-else class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div v-if="message" class="flex items-center justify-between mb-6">
              <span v-if="complete == 'completed'" style="color:green">
                <p>{{ message }} </p>
              </span>
              <span v-else>
                <p style="color: red">{{ message }} </p>
              </span>
            </div>
            <div v-else class="flex items-center justify-between mb-6">
              <span>
                <p>Click the refresh button.</p>
              </span>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button
                v-if="!merchantRequestId"
                type="button"
                class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2
                bg-orange-500 text-base leading-6 font-medium text-white shadow-sm hover:bg-orange-500
                focus:outline-none focus:border-orange-700 focus:shadow-outline-orange sm:text-sm
                sm:leading-5"
                @click="confirmMakePaymentModel()"
              >
                Send
              </button>
              <button v-else type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2
                bg-green-500 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500
                focus:outline-none focus:border-green-700 focus:shadow-outline-green sm:text-sm
                sm:leading-5" @click="checkPayment()">
                  Refresh
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closePaymentModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!--modal content-->
  <div v-if="isOrderConfirmOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <p>
                You are about to confirm the order <strong>{{ formConfirm.order_no }}</strong>. Proceed?
              </p>
            </div>
          </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
            <button
              type="button"
              class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2
                bg-orange-500 text-base leading-6 font-medium text-white shadow-sm hover:bg-orange-500
                focus:outline-none focus:border-orange-700 focus:shadow-outline-orange sm:text-sm
                sm:leading-5"
                @click="makeConfirmOrder()"
              >
              Yes
            </button>
          </span>
          <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
            <button @click="closeConfirmOrderModel" type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5">
              Cancel
            </button>
          </span>
        </div>
        </form>
      </div>
    </div>
  </div>
  <!--modal content-->
  <div v-if="isConfirmPaymentOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <p>
                You are about to initiate payment from <strong>{{ form.customer_name }} {{ form.phone_number }}</strong>. Proceed?
              </p>
            </div>
          </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
            <button
              type="button"
              class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2
                bg-orange-500 text-base leading-6 font-medium text-white shadow-sm hover:bg-orange-500
                focus:outline-none focus:border-orange-700 focus:shadow-outline-orange sm:text-sm
                sm:leading-5"
                @click="makePaymentOrder()"
              >
              Yes
            </button>
          </span>
          <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
            <button @click="closeConfirmMakePaymentModel" type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5">
              Cancel
            </button>
          </span>
        </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="showAddPayment" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-3/4" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 class="text-3xl font-bold">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6" style="display: inline !important;">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
              </svg>

              Add Payment Details
            </h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <form>
                <div>
                  <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-2 pr-8 pb-8">
                    <p class="pb-1">Select Mode of Payment</p>
                    <multiselect
                      id="provider"
                      v-model="formPaymentData.payment_provider"
                      placeholder="Search Mode of Payment"
                      :options="providers"
                      :clear-on-select="false"
                      :preserve-search="true"
                      :searchable="true"
                      class="mt-1 w-1/2"
                      :close-on-select="true"
                      :create-option="true"
                      :error="$props.errors.payment_provider"
                      :style="$props.errors.payment_provider ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
                    />
                    <p v-if="$props.errors" class="text-red-700 pt-2"><small>{{ $props.errors.payment_provider }}</small></p>
                  </div>
                  <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-2 pr-2 pb-2">
                    <text-input v-model="formPaymentData.date" type="date" :error="$props.errors.date" class="pb-8 pr-6 w-1/3 lg:w-1/3" label="Date" />
                    <text-input v-model="formPaymentData.reference" :error="$props.errors.reference" class="pb-8 pr-6 w-1/3 lg:w-1/3" label="Reference" />
                    <text-input v-model="formPaymentData.amount" type="number" :error="$props.errors.amount" class="pb-8 pr-6 w-1/3 lg:w-1/3" label="Amount" />
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="storePayment">
                Submit
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="askDelete" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 20px;">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6" style="display: inline !important; cursor: pointer;" @click="openDeletePrompt(order)">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>

              Cancel Order #{{ order.order_no }}
            </h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              You are about to cancel the order {{ order.order_no }}, do you wish to continue?
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="cancel">
                Cancel Order
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import Multiselect from '@vueform/multiselect'
import SelectInput from '@/Shared/SelectInput'
import axios from 'axios'

export default {
  components: {
    Head,
    Icon,
    Link,
    TextInput,
    Multiselect,
    SelectInput,
  },
  layout: Layout,
  props: {
    order: Object,
    providers: Object,
    errors: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        order_id: null,
        order_no: null,
        phone_number: null,
        amount: null,
        customer_name: null,
      }),
      orderObj: [],
      formPaymentData: {
        'date': null,
        'reference': null,
        'amount': null,
        'payment_provider': null,
      },
      formConfirm: {
        order_id: null,
        order_no: null,
      },
      stepOne: false,
      stepTwo: false,
      askDelete: false,
      showAddPayment: false,
      isMakePaymentOpen: false,
      isConfirmPaymentOpen: false,
      mpesa_response: null,
      status: 0,
      merchantRequestId: null,
      amountError: null,
      phonenumberError: null,
      loading: false,
      complete: null,
      message: null,
      vouchers: [],
      isConfirmOrderCreation: false,
      isOrderConfirmOpen: false,
      isConfirmOrderUpdate: false,
      popUpErrors: null,
      replaceRescheduleStatuses: [
        'Picklisted', 'Picked', 'Partially Picked',
        'Packed', 'Partially Packed', 'Loaded',
      ],
    }
  },
  computed: {
    amountToPay() {
      const totalPaid = this.order.total_payments ? this.order.total_payments.replace(/,/g,"") : 0
      const amountToPay = this.order.amount ? this.order.amount.replace(/,/g,""): 0
      const total = (amountToPay - totalPaid)
      this.form.amount = total
      return total
    },
    user() {
      return this.$inertia.page.props.auth.user
    },
    totalOrder() {
      let totOrder = 0
      if (this.order.details) {
        this.order.details.forEach((amount)=>{ 
          totOrder += parseFloat(amount.amount)
        })
      }
      return totOrder
    },
  },
  methods: {
    closeConfirmMakePaymentModel() {
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.openEditOrder = false
      this.isMakePaymentOpen = true
      this.isConfirmPaymentOpen = false
    },
    closeModal() {
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.openEditOrder = false
      this.isMakePaymentOpen = false
      this.isConfirmPaymentOpen = false
    },
    closeConfirmMakePaymentModel() {
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.openEditOrder = false
      this.isMakePaymentOpen = true
      this.isConfirmPaymentOpen = false
    },
    makePaymentModel(order) {
      this.form.order_id = order.id
      this.form.customer_name = order.customer
      this.form.order_on = order.order_no
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = false
      this.isMakePaymentOpen = true
    },
    confirmPlacedOrderModal(order) {
      this.formConfirm.order_id = order.id
      this.formConfirm.order_no = order.order_no
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = false
      this.isMakePaymentOpen = false
      this.isOrderConfirmOpen = true
    },
    closeConfirmOrderModel() {
      this.popUpErrors = null
      this.openCreateOrder = false
      this.openDetail = true
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = false
      this.isMakePaymentOpen = false
      this.isOrderConfirmOpen = false

    },
    closeConfirmOrderUpdateModel() {
      this.openCreateOrder = false
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = false
      this.isMakePaymentOpen = false
      this.isOrderConfirmOpen = false
      this.isConfirmOrderUpdate = false
    },
    confirmMakePaymentModel() {
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = true
      this.isMakePaymentOpen = true
      this.mpesa_response = null
      this.loading = false
      this.status = 0
    },
    confirmOrderModal() {
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = false
      this.isMakePaymentOpen = false
      this.openCreateOrder = true
      this.isConfirmOrderCreation = true
    },
    closeConfirmOrderModel() {
      this.openCreateOrder = true
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = false
      this.isMakePaymentOpen = false
      this.isConfirmOrderCreation = false
      this.isOrderConfirmOpen = false
    },
    confirmOrderUpdateModal() {
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = false
      this.isMakePaymentOpen = false
      this.openEditOrder = true
      this.isConfirmOrderUpdate = true
    },
    closePaymentModal() {
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isMakePaymentOpen = false
      this.mpesa_response = null
      this.form.phone_number = null
      this.isConfirmPaymentOpen = false
      this.merchantRequestId = null
      this.message = null
      this.complete = null
      window.location.reload()
    },
    checkPayment() {
      const data = {}
      data.merchant_request_id = this.merchantRequestId
      const self = this
      axios.post('/payments/check-payment', data).then(response => {
        if (response) {
          self.message = response.data.message
          self.complete = response.data.status
          self.isMakePaymentOpen = true
          self.status = null
          self.mpesa_response= null
        }
      }).catch(function(error) {

      })
    },
    makePaymentOrder() {
      const data = {}
      data.order_id = this.order.id
      data.phone_number = this.form.phone_number
      data.amount = this.form.amount
      data._method = 'POST'
      const self = this
      self.loading = true;
      this.isConfirmPaymentOpen = false
      axios.post('/payments/process', data).then(response => {
        if (response) {
          self.show = true
          self.mpesa_response = response.data.message
          self.status = response.data.status
          self.stepOne = true
          self.stepTwo = false
          self.openCreateOrder = false
          self.isMakePaymentOpen = true
          self.isConfirmPaymentOpen = false
          self.merchantRequestId = response.data.merchant_request_id
        }
      }).catch(function(error) {
        if (error.response.status === 422) {
          const { amount, phone_number } = error.response.data.errors
          if (phone_number) {
            const [n] = phone_number;
            self.phonenumberError = n
          }
          if (amount) {
            const [a] = amount;
            self.amountError = a
          }
        }
        self.isConfirmPaymentOpen = false
        self.isMakePaymentOpen = true
      }).then(() => {
        self.loading = false;
      })
    },
    makeConfirmOrder() {
      const data = {}
      data.order_id = this.order.id
      data._method = 'POST'
      this.$inertia.post('/online-orders/confirm', data, {
        onSuccess: () => {
          this.formConfirm.order_id = null
          this.formConfirm.order_no = null
          this.isOrderConfirmOpen = false
          this.openDetail = false
        },
      })
    },
    storePayment() {
      this.formPaymentData.order_id = this.order.id
      this.formPaymentData._method = 'post'
      this.$inertia.post('/order-payments', this.formPaymentData, {
        onSuccess: () => {
          this.formPaymentData.date = null
          this.formPaymentData.amount = null
          this.formPaymentData.payment_provider = null
          this.formPaymentData.reference = null
          this.showAddPayment = false
          this.isMakePaymentOpen = false
          this.isConfirmPaymentOpen = false
        }
      })
    },
    openDeletePrompt(item) {
      this.askDelete = true
      this.selectedOrder = item
      this.isMakePaymentOpen = false
    },
    addPayment(item) {
      this.showAddPayment = true
      this.isMakePaymentOpen = false
    },
    cancel() {
      let cancelData = {}
      cancelData._method = 'post'
      cancelData.order = this.order.id
      this.$inertia.post(`/cancel-order/${this.order.id}`, cancelData, {
        onSuccess: () => {
          this.askDelete = false
          this.isMakePaymentOpen = false
          this.isConfirmPaymentOpen = false
        },
      })
    },
  },
}
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
  .multiselect-option.is-selected.is-pointed {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .multiselect-option.is-selected {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .modal-wrapper h1 {
    font-size: 20px;
    font-weight:bold;
    margin-bottom: 10px;
    margin-top: 10px;
  }
  .approved {
    color: green;
  }
  .declined {
    color: red;
  }
</style>